import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { TemplateWrapper } from "./styles";
import {
	IGallery,
	INVITATION_OCCASION,
	INVITATION_TYPES,
	Invitations,
	LangType
} from "../../../../store/services/invitations/dto/invitations.dto";
import { TemplateColorObject } from "../../../../InvitationColorMatrix";
import { LANG_AR, LANG_EN, RTL } from "../constants";
import langEN from "../lang.EN.json";
import langAR from "../lang.AR.json";
import { getFieldMap } from "../../../../utils/inputs";
import { Box } from "@mui/material";
import { getWeddingDate } from "../../../../utils/formatDate";
import CountDown from "../CountDown";
import InvitationGallery from "../../../InvitationGallery";
import Comments from "../Components/Comments";
import MapComponent from "../Components/MapComponent/MapComponent";
import MapButton from "../Components/MapButton";
import SaveTheDateBtn from "../Components/SaveTheDateBtn";
import PartnersSocialList from "../Components/PartnersSocialList";
import Footer from "../Components/Footer";

const FloralSVG = () => (
	<svg viewBox="0 0 200 200" className="floral-svg" xmlns="http://www.w3.org/2000/svg">
		<g opacity="0.35">
			<circle cx="100" cy="100" r="8" fill="#B76E79" />
			{[0, 45, 90, 135, 180, 225, 270, 315].map((a, i) => (
				<ellipse
					key={i}
					cx="100"
					cy="100"
					rx="6"
					ry="22"
					transform={`rotate(${a} 100 100) translate(0,-28)`}
					fill="#4d0c41"
				/>
			))}
			{[0, 60, 120, 180, 240, 300].map((a, i) => (
				<ellipse
					key={i}
					cx="100"
					cy="100"
					rx="4"
					ry="15"
					transform={`rotate(${a} 100 100) translate(0,-48)`}
					fill="#4d0c41"
					opacity="0.5"
				/>
			))}
		</g>
	</svg>
);

const Template06 = forwardRef(
	(
		{
			data,
			gallery,
			colorMatrix,
			canEdit = false
		}: {
			data: Invitations;
			gallery: IGallery[];
			colorMatrix: TemplateColorObject;
			canEdit?: boolean;
			setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
		},
		ref
	) => {
		const isTemplate = data?.type === INVITATION_TYPES.TEMPLATE;
		const invitationId = data?._id || "";
		const lang = data?.language === LANG_EN ? langEN : langAR;
		const fieldMap = getFieldMap(invitationId);
		const commentsSectionRef = useRef<HTMLElement>(null);
		const locationSectionRef = useRef<HTMLElement>(null);
		useImperativeHandle(ref, () => ({
			scrollToComments: () => {
				commentsSectionRef.current && commentsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			},
			scrollToLocation: () => {
				locationSectionRef.current && locationSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}));

		const invitation_type_title = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.the_engagement_of;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.the_katb_ktab_of;
				}
				default: {
					return lang.the_wedding_of;
				}
			}
		}, [data]);

		const invitation_getting_text = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.GETTING_ENGAGED;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.GETTING_KATB_KTAB;
				}
				default: {
					return lang.GETTING_MARRIED;
				}
			}
		}, [data]);

		const invitation_location_header = useMemo(() => {
			switch (data?.occasion) {
				case INVITATION_OCCASION.ENGAGEMENT: {
					return lang.engagement_location;
				}
				case INVITATION_OCCASION.KATB_KTAB: {
					return lang.katb_ktab_location;
				}
				default: {
					return lang.wedding_location;
				}
			}
		}, [data]);

		return (
			<>
				<TemplateWrapper id={"template_wrapper"} colormatrix={colorMatrix}>
					{/* SECTION 01 - HERO */}
					<section className="section_01">
						<img
							src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ycAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAEAAAAALAAAAAABAAEAAAICTAEAOw=="
							alt=""
							style={{ display: "none" }}
							id={data?.fieldVisibility?.main_image?.hidden ? "main_image" : ""}
						/>
						{data?.has_images && (canEdit || !data?.fieldVisibility?.main_image?.hidden) && (
							<div className="main_image-wrap">
								<img
									data-dispatch-id={fieldMap.MAIN_IMAGE}
									src={data?.main_image}
									id={"main_image"}
									className={`main_image ${data?.fieldVisibility?.main_image?.hidden ? "shaded" : ""}`}
									alt="Couple"
								/>

								{/*<div className="hero-overlay" />*/}
								<Box className={"fader"}></Box>
							</div>
						)}
						<div className="hero-content">
							<div className="hero-floral hero-floral--top">
								<FloralSVG />
							</div>
							<div className="hero-floral hero-floral--bottom">
								<FloralSVG />
							</div>
							<p className="hero-intro-tag" style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}>
								— {invitation_type_title} —
							</p>

							<h1
								data-dispatch-id={fieldMap.GROOM_NAME}
								className={"guide_edit_field groom_name animate-on-scroll delay-1"}
							>
								{data.groom_name}
							</h1>

							<Box height={10} />
							<PartnersSocialList
								socials={data?.partner_socials_1 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_1}
							/>

							<div className="hero-ampersand">&amp;</div>

							<h1 data-dispatch-id={fieldMap.BRIDE_NAME} className={"bride_name animate-on-scroll delay-2"}>
								{data.bride_name}
							</h1>

							<Box height={10} />
							<PartnersSocialList
								socials={data?.partner_socials_2 || []}
								canEdit={canEdit}
								dispatchId={fieldMap.SOCIAL_MEDIA_2}
							/>
							<div className="hero-divider">
								<span className="hero-divider__line" />
								<span className="hero-divider__diamond">◆</span>
								<span className="hero-divider__line" />
							</div>
							<Box height={10} />
							<p
								data-dispatch-id={fieldMap.WEDDING_DATE}
								className="date"
								style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}
							>
								{getWeddingDate(data?.wedding_date, data?.language, data?.timezone, isTemplate)}
							</p>

							<SaveTheDateBtn canEdit={canEdit} data={data!} btnText={lang.save_the_date_button_text} />
							<Box height={20} />
						</div>
					</section>

					{/*SECTION 02 - COUNTDOWN */}
					<section className="section_02">
						{data?.is_islamic && (
							<p className="countdown-title">
								بَارَكَ اللهُ لَكُما، وَبَارَكَ عَلَيْكُما، وَجَمَعَ بَيْنَكُما فِي خَيْر
							</p>
						)}
						<h2 className="countdown-heading">{invitation_getting_text}</h2>
						<Box className={"count_down_wrapper"}>
							<CountDown
								data={data || ({} as Invitations)}
								daysString={lang.days}
								hoursString={lang.hours}
								minutesString={lang.mins}
								secondsString={lang.sec}
							/>
						</Box>
					</section>

					{/* SECTION 03 - INTRO */}
					<section className="section_03">
						{/*<p className="quran">{data.quran}</p>*/}
						{(canEdit || !data?.fieldVisibility?.intro_text?.hidden) && (
							<>
								<p
									data-dispatch-id={fieldMap.INTRO_TEXT}
									className={`main_intro ${data?.fieldVisibility?.intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data.intro_text}
								</p>
							</>
						)}
						<Box height={10} />

						{(canEdit || !data?.fieldVisibility?.sub_intro_text?.hidden) && (
							<>
								<p
									data-dispatch-id={fieldMap.SUB_INTRO}
									className={`sub_intro ${data?.fieldVisibility?.sub_intro_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.sub_intro_text}
								</p>
								<Box height={20} />
							</>
						)}
						<Box height={20} />
						{data?.has_images && (
							<div className="couple-images">
								<div className="couple-card">
									{(canEdit || !data?.fieldVisibility?.groom_image?.hidden) && (
										<img
											data-dispatch-id={fieldMap.GROOM_IMAGE}
											className={`partner_img ${data?.fieldVisibility?.groom_image?.hidden ? "shaded" : ""}`}
											src={data?.groom_image}
											alt="groom"
										/>
									)}

									<p
										data-dispatch-id={fieldMap.GROOM_NAME}
										className="couple-name"
										style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}
									>
										{data?.groom_name}
									</p>
								</div>
								<div className="couple-heart">♡</div>
								<div className="couple-card">
									{(canEdit || !data?.fieldVisibility?.bride_image?.hidden) && (
										<img
											data-dispatch-id={fieldMap.BRIDE_IMAGE}
											className={`partner_img ${data?.fieldVisibility?.bride_image?.hidden ? "shaded" : ""}`}
											src={data?.bride_image}
											alt="groom"
										/>
									)}

									<p
										data-dispatch-id={fieldMap.BRIDE_NAME}
										className="couple-name"
										style={{ fontSize: data?.language === LANG_AR ? "25px" : "" }}
									>
										{data?.bride_name}
									</p>
								</div>
							</div>
						)}
					</section>

					{/* SECTION 04 - QURAN */}
					{data?.is_islamic && (
						<section className="section_04">
							<div className="quran-card">
								<div className="quran-ornament">✦ ✦ ✦</div>
								<p className="bismillah">بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْم</p>
								<p className="verse">
									﴿ وَمِنْ آيَاتِهِ أَنْ خَلَقَ لَكُم مِّنْ أَنفُسِكُمْ أَزْوَاجًا لِّتَسْكُنُوا إِلَيْهَا وَجَعَلَ
									بَيْنَكُم مَّوَدَّةً وَرَحْمَةً ۚ إِنَّ فِي ذَٰلِكَ لَآيَاتٍ لِّقَوْمٍ يَتَفَكَّرُون ﴾
								</p>
								<p className="ending">صدق الله العظيم</p>
								<div className="quran-ornament">✦ ✦ ✦</div>
							</div>
						</section>
					)}

					{/* SECTION 05 - LOVE STORY */}
					{!data?.fieldVisibility?.loveStory?.hidden && (
						<section className="section_05">
							{data?.has_images && (canEdit || !data?.fieldVisibility?.story_image?.hidden) && (
								<>
									<img
										data-dispatch-id={fieldMap.STORY_IMAGE}
										src={data?.story_image}
										className={`story_image ${data?.fieldVisibility?.story_image?.hidden ? "shaded" : ""}`}
										alt="Love Story"
									/>

									<Box height={40} />
								</>
							)}
							<h2 className="section_title">{lang.our_love_story}</h2>
							<div className="story-divider">
								<span />
								<span className="story-divider__icon">💕</span>
								<span />
							</div>
							<div className={`stories ${data?.language === LANG_AR ? RTL : ""}`} data-dispatch-id={fieldMap.STORY}>
								{data?.story.length > 0 &&
									data?.story.map((story, index) => (
										<div className={`story-item animate-on-scroll delay-${index + 1}`} key={index}>
											<div className="story-item__num">{String(index + 1).padStart(2, "0")}</div>
											<div className="story-item__body">
												<h3 className={`story-item__title ${data?.language === LANG_AR ? RTL : ""}`}>{story.title}</h3>
												<p className={`story-item__content ${data?.language === LANG_AR ? RTL : ""}`}>
													{story.description}
												</p>
											</div>
										</div>
									))}
							</div>
						</section>
					)}

					{/* SECTION 06 - GALLERY */}
					{!data?.fieldVisibility?.gallery?.hidden && data?.has_images && (
						<section className="section_06">
							<h2 className="section_title">{lang.our_gallery}</h2>
							<div className="story-divider">
								<span />
								<span className="story-divider__icon">🌸</span>
								<span />
							</div>

							<Box data-dispatch-id={fieldMap.GALLERY}>
								<InvitationGallery gallery={gallery || []} canEdit={canEdit} />
							</Box>
						</section>
					)}

					{/* SECTION 07 - COMMENTS */}
					{!data?.fieldVisibility?.comments?.hidden && (
						<section className="section_07" ref={commentsSectionRef}>
							<h2 className="section_title">{lang.comments}</h2>

							{(canEdit || !data?.fieldVisibility?.comments_text?.hidden) && (
								<h3
									data-dispatch-id={fieldMap.COMMENTS_TEXT}
									className={`comment_title ${data?.fieldVisibility?.comments_text?.hidden ? "shaded" : ""} animate-on-scroll delay-1`}
								>
									{data.comments_text}
								</h3>
							)}
							<Box height={15} />
							{(canEdit || !data?.fieldVisibility?.comments_sub_text?.hidden) && (
								<p
									data-dispatch-id={fieldMap.COMMENTS_SUB_TEXT}
									className={`comment_description ${data?.fieldVisibility?.comments_sub_text?.hidden ? "shaded" : ""} animate-on-scroll delay-2`}
								>
									{data?.comments_sub_text}
								</p>
							)}
							<Comments canEdit={canEdit} language={data?.language as LangType} colormatrix={colorMatrix} />
						</section>
					)}

					{/* SECTION 08 - LOCATION */}
					{!data?.fieldVisibility?.location?.hidden && (
						<section className="section_08" ref={locationSectionRef}>
							<h2 className="section_title">{invitation_location_header}</h2>
							{(canEdit || !data?.fieldVisibility?.wedding_location_text?.hidden) && (
								<p
									data-dispatch-id={fieldMap.LOCATION_TEXT}
									className={`location_name ${data?.fieldVisibility?.wedding_location_text?.hidden ? "shaded" : ""}`}
								>
									{data?.wedding_location_text}
								</p>
							)}
							<Box height={20} />
							<Box height={20} />

							<Box data-dispatch-id={fieldMap.MAP}>
								<MapComponent canEdit={canEdit} />
							</Box>

							<Box height={20} />
							<Box data-dispatch-id={fieldMap.MAP}>
								<MapButton canEdit={canEdit} text={lang.location} />
							</Box>
							<div className="footer-floral">
								<FloralSVG />
							</div>
						</section>
					)}

					{/* SECTION 09 - FOOTER */}

					<Footer colormatrix={colorMatrix} />
				</TemplateWrapper>
			</>
		);
	}
);

export default Template06;
